import React from 'react';
import { useTranslation } from 'react-i18next';

import { Nav, Arrow, Divider } from './StepNav.styles';

const ChevronLeft = (): React.ReactElement => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronRight = (): React.ReactElement => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export type StepNavProps = {
  /** Browse one card toward the start (completed history). */
  onPrev: () => void;
  /** Browse one card toward the end (pending steps / the live edge). */
  onNext: () => void;
  /** At the very start — the left chevron reads disabled. */
  atStart: boolean;
  /** At the very end — the right chevron reads disabled. */
  atEnd: boolean;
};

/**
 * The deploy strip's browse control: a compact frosted pill with the left/right chevrons and
 * a slim horizontal separator between them. It lives in the section's top-right corner (see
 * the header). The chevrons step one card; the strip otherwise auto-follows the live step and
 * yields to manual scroll. There is deliberately NO play/pause control — a pause button would
 * read as pausing the deploy itself, which it does not.
 */
export const StepNav = ({ onPrev, onNext, atStart, atEnd }: StepNavProps): React.ReactElement => {
  const { t } = useTranslation();

  return (
    <Nav>
      <Arrow
        type="button"
        aria-label={t('Previous steps')}
        aria-disabled={atStart}
        onClick={() => {
          if (!atStart) onPrev();
        }}
      >
        <ChevronLeft />
      </Arrow>

      <Divider aria-hidden="true" />

      <Arrow
        type="button"
        aria-label={t('Next steps')}
        aria-disabled={atEnd}
        onClick={() => {
          if (!atEnd) onNext();
        }}
      >
        <ChevronRight />
      </Arrow>
    </Nav>
  );
};

export default StepNav;
